/**
 * The app's auth hook over the active auth source (Clerk or a Lamma session).
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import i18next from 'i18next';
import toast from 'react-hot-toast';

import { apiClient } from '../../../core/config/api';
import { useAuthSource } from '../contexts/authSourceContext';
import type { User } from '../types/auth';
import { clearLessonHistoricalEventsCache } from '../../lesson/hooks/useLessonGenerationMode';
import { mediaApiService } from '../../media/services/media_api';

export const useAuth = () => {
  const { kind, isLoaded, isSignedIn, accessToken, getToken, signOut, endSession } = useAuthSource();

  // Fetch user data from our backend
  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useQuery<User>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await apiClient.get('/auth/me');
      return response.data.user;
    },
    enabled: !!accessToken && isSignedIn,
    retry: 1,
  });

  // Handle user fetch errors
  useEffect(() => {
    if (userError) {
      console.error('Failed to fetch user data:', userError);
      const error = userError as any;
      if (error.response?.status === 401) {
        // A Lamma session that ended is handled by its source, which leaves for the session-ended page
        if (kind === 'lamma') return;
        toast.error('Session expired. Please log in again.');
        logout();
      } else {
        toast.error('Failed to load user data.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userError]);

  const clearLocalSession = async () => {
    delete apiClient.defaults.headers.authorization;
    clearLessonHistoricalEventsCache();
    // Fire-and-forget cookie clear, called directly like the rest of logout: no cached state to own.
    await mediaApiService.clearCdnCookie().catch(() => undefined);
  };

  // Logout function
  const logout = async () => {
    await clearLocalSession();
    const result = await signOut();

    if (kind === 'lamma') {
      if (result.ended) {
        window.location.assign(result.leaveTo ?? '/login');
      } else {
        toast.error(i18next.t('common:errors.logoutFailed'));
      }
      return;
    }

    toast.success('Logged out successfully');
  };

  // Ends any signed-in session without navigating, so another user can sign in on the current page
  const endSessionForUserSwitch = async (): Promise<boolean> => {
    if (!isSignedIn) return false;
    await clearLocalSession();
    return endSession();
  };

  // Update user profile
  const updateProfile = async (profileData: Partial<User>) => {
    try {
      const response = await apiClient.post('/auth/update-profile', profileData);
      toast.success('Profile updated successfully');
      await refetchUser();
      return response.data.user;
    } catch (error: any) {
      console.error('Profile update failed:', error);
      const message = error.response?.data?.error || 'Failed to update profile';
      toast.error(message);
      throw error;
    }
  };

  // Signed-in users must have a resolved user object (or a definitive error)
  // before guards can make navigation decisions. Without this, a failed /auth/me
  // query leaves isLoading=false + user=undefined, and guards silently skip onboarding.
  const isUserSettled = !isSignedIn || !accessToken || !!user || !!userError;

  return {
    // Authentication state
    isAuthenticated: isSignedIn && !!accessToken,
    isLoading: !isLoaded || (isSignedIn && !accessToken) || userLoading || !isUserSettled,

    // User data
    user,
    userError,

    // Functions
    updateProfile,
    logout,
    endSessionForUserSwitch,
    refetchUser,

    // Token for making authenticated requests
    accessToken,

    // Token getter for WebSocket connections (always fetches fresh token)
    getAuthToken: getToken,
  };
};
